import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, finalize } from 'rxjs';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { CbpayCardexService } from '../../services/cbpay-cardex.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';

@Component({
  selector: 'app-cbpay-cardex',
  templateUrl: './cbpay-cardex.component.html',
  styleUrls: ['./cbpay-cardex.component.sass'],
})
export class CbpayCardexComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  movements: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  page: number = 1;
  limit: number = 10;
  totalPages: number = 0;
  total: number = 0;
  from: Date | null = null;
  to: Date | null = null;
  errorMessage: string = '';
  dialogRef: MatDialogRef<SpinnerComponent> | null = null;

  columns = [
    {
      columnDef: 'transactionId',
      header: 'Transaction ID',
      cell: (element: any) => `${element.transactionId}`,
    },
    {
      columnDef: 'transactionDate',
      header: 'Fecha',
      cell: (element: any) =>
        new Date(element.transactionDate).toLocaleString(),
    },
    {
      columnDef: 'type',
      header: 'Tipo',
      cell: (element: any) => element.type,
    },
    {
      columnDef: 'debitUsd',
      header: 'Débito USD',
      cell: (element: any) => this.formatNumber(element.debitUsd),
    },
    {
      columnDef: 'creditUsd',
      header: 'Crédito USD',
      cell: (element: any) => this.formatNumber(element.creditUsd),
    },
    {
      columnDef: 'balanceUsd',
      header: 'Balance USD',
      cell: (element: any) => this.formatNumber(element.balanceUsd),
    },
    {
      columnDef: 'description',
      header: 'Descripción',
      cell: (element: any) => element.description,
    },
  ];

  displayedColumns = this.columns.map((c) => c.columnDef);

  expandedCardId: number | null = null;

  constructor(
    private cardexService: CbpayCardexService,
    private spinnerService: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.loadMovements();
  }

  private async handleOperation<T>(
    operation: () => Promise<Observable<T>>,
    errorMessage: string,
    successCallback: (result: T) => void,
  ): Promise<void> {
    this.dialogRef = this.spinnerService.start();
    try {
      const observable = await operation();
      observable
        .pipe(
          finalize(() => {
            if (this.dialogRef) {
              this.dialogRef.close();
            }
          }),
        )
        .subscribe({
          next: (result: T) => {
            successCallback(result);
            this.errorMessage = '';
          },
          error: (error: any) => {
            this.errorMessage = errorMessage;
          },
        });
    } catch (error) {
      this.errorMessage = errorMessage;
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }
  }

  async loadMovements(): Promise<void> {
    await this.handleOperation(
      () =>
        this.cardexService.listCardexMovements({
          page: this.page,
          limit: this.limit,
          startDate: this.formatDateForAPI(this.from),
          endDate: this.formatDateForAPI(this.to),
        }),
      'Error al cargar los movimientos',
      (response: any) => {
        this.movements = response.data;
        this.dataSource = new MatTableDataSource(this.movements);
        this.total = response.meta.total;
        this.totalPages = response.meta.totalPages;
      },
    );
  }

  async exportToExcel(): Promise<void> {
    await this.handleOperation(
      () =>
        this.cardexService.exportCardexToExcel({
          startDate: this.formatDateForAPI(this.from),
          endDate: this.formatDateForAPI(this.to),
        }),
      'Error al exportar el archivo',
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'cbpay_cardex.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
    );
  }

  onPageChange(event: PageEvent): void {
    if (event) {
      this.page = event.pageIndex + 1;
      this.limit = event.pageSize;
      this.loadMovements();
    }
  }

  formatNumber(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toFixed(8);
  }

  async reload(): Promise<void> {
    this.page = 1;
    this.loadMovements();
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  private formatDateForAPI(date: Date | null): string | undefined {
    if (!date) return undefined;
    
    // Asegurar que la fecha se envíe en formato ISO (YYYY-MM-DD) al inicio del día
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    return localDate.toISOString().split('T')[0];
  }
}
